import React, { useState } from 'react';
import { FolderOpen, MessageCircle, Calendar, ExternalLink, FileEdit as Edit2, Save, X, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/auth';

interface ClientToolsCardProps {
  client: {
    id: string;
    drive_url: string | null;
    whatsapp_url: string | null;
    calendly_url: string | null;
    pipedrive_deal_id: string | null;
    sales_emails: string[] | null;
  };
  onUpdate: (field: string, value: string) => Promise<void>;
}

interface EditingField {
  field: string;
  value: string;
}

const ClientToolsCard: React.FC<ClientToolsCardProps> = ({ client, onUpdate }) => {
  const [editing, setEditing] = useState<EditingField | null>(null);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);

  const tools = [
    {
      key: 'drive_url',
      label: 'Drive',
      icon: FolderOpen,
      color: 'blue',
      buttonLabel: 'Ouvrir Drive',
      value: client.drive_url,
      buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    },
    {
      key: 'whatsapp_url',
      label: 'WhatsApp',
      icon: MessageCircle,
      color: 'green',
      buttonLabel: 'Ouvrir WhatsApp',
      value: client.whatsapp_url,
      buttonClass: 'bg-green-600 hover:bg-green-700 text-white',
    },
    {
      key: 'calendly_url',
      label: 'Calendly',
      icon: Calendar,
      color: 'purple',
      buttonLabel: 'Ouvrir Calendly',
      value: client.calendly_url,
      buttonClass: 'bg-purple-600 hover:bg-purple-700 text-white',
    },
  ];

  const handleEdit = (field: string, currentValue: string | null) => {
    setEditing({ field, value: currentValue || '' });
  };

  const handleCancel = () => {
    setEditing(null);
  };

  const handleSave = async () => {
    if (!editing) return;

    try {
      setSaving(true);
      await onUpdate(editing.field, editing.value);
      setEditing(null);
    } catch (error) {
      console.error('Error saving field:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (value: string) => {
    if (editing) {
      setEditing({ ...editing, value });
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;

    try {
      const currentEmails = client.sales_emails || [];
      const updatedEmails = [...currentEmails, newEmail.trim()];

      const { error } = await supabase
        .from('clients')
        .update({ sales_emails: updatedEmails })
        .eq('id', client.id);

      if (error) throw error;

      setNewEmail('');
      setAddingEmail(false);
      window.location.reload();
    } catch (error) {
      console.error('Error adding email:', error);
    }
  };

  const handleRemoveEmail = async (emailToRemove: string) => {
    try {
      const currentEmails = client.sales_emails || [];
      const updatedEmails = currentEmails.filter(e => e !== emailToRemove);

      const { error } = await supabase
        .from('clients')
        .update({ sales_emails: updatedEmails })
        .eq('id', client.id);

      if (error) throw error;

      window.location.reload();
    } catch (error) {
      console.error('Error removing email:', error);
    }
  };

  const pipedriveUrl = client.pipedrive_deal_id
    ? `https://app.pipedrive.com/deal/${client.pipedrive_deal_id}`
    : null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Outils</h3>

      <div className="space-y-4">
        {tools.map((tool) => {
          const Icon = tool.icon;
          const isEditing = editing?.field === tool.key;
          const hasValue = tool.value && tool.value.trim() !== '';

          return (
            <div key={tool.key} className="flex items-start gap-3">
              <Icon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />

              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-500 mb-2">
                  {tool.label}
                </div>

                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={editing.value}
                      onChange={(e) => handleChange(e.target.value)}
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSave();
                        if (e.key === 'Escape') handleCancel();
                      }}
                    />
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Sauvegarder"
                    >
                      <Save className="h-4 w-4" />
                    </button>
                    <button
                      onClick={handleCancel}
                      className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      title="Annuler"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : hasValue ? (
                  <div className="flex items-center gap-2">
                    <a
                      href={tool.value || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tool.buttonClass}`}
                    >
                      {tool.buttonLabel}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <button
                      onClick={() => handleEdit(tool.key, tool.value)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleEdit(tool.key, tool.value)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Ajouter un lien
                    <Edit2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {pipedriveUrl && (
          <div className="flex items-start gap-3">
            <ExternalLink className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-500 mb-2">
                Pipedrive
              </div>
              <a
                href={pipedriveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-orange-600 hover:bg-orange-700 text-white"
              >
                Ouvrir Pipedrive
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        )}

        <div className="flex items-start gap-3">
          <MessageCircle className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-500 mb-2">
              Emails sales
            </div>
            <div className="space-y-2">
              {(client.sales_emails || []).map((email, index) => (
                <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                  <span className="flex-1 text-sm text-gray-900">{email}</span>
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Supprimer"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {addingEmail ? (
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddEmail();
                      if (e.key === 'Escape') { setAddingEmail(false); setNewEmail(''); }
                    }}
                  />
                  <button
                    onClick={handleAddEmail}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Ajouter"
                  >
                    <Save className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { setAddingEmail(false); setNewEmail(''); }}
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title="Annuler"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setAddingEmail(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Ajouter un email
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Les liens vous permettent d'accéder rapidement aux outils externes associés à ce client.
        </p>
      </div>
    </div>
  );
};

export default ClientToolsCard;
